<template>
  <div>
    <AppLoadingPage
      v-if="cashierLoading || eventLoading"
      custom-text="Loading details"
    />
    <template v-else>
      <div v-if="$vuetify.breakpoint.mdAndUp" class="cashier-details">
        <h3 class="pa-3">{{ person.name }}</h3>
        <table>
          <colgroup>
            <col style="width: 165px" />
          </colgroup>
          <colgroup>
            <col style="width: 150px" />
            <col style="width: 150px" />
            <col style="width: 150px" />
            <col style="width: 88px" />
          </colgroup>
          <thead>
            <tr>
              <th></th>
              <th colspan="4" class="text-center">
                {{
                  $t(
                    "wallet:finances.Cashier.RegistrationsAndProducts.label",
                    "Registrations and products"
                  )
                }}
              </th>
              <template v-if="event.useOnlinePaymentsForPrizeMoney">
                <th colspan="3" class="text-center">
                  {{
                    $t(
                      "wallet:finances.Cashier.PrizeMoney.label",
                      "Prize money"
                    )
                  }}
                </th>
              </template>
            </tr>
            <tr>
              <th>
                {{ $t("wallet:finances.Cashier.Item.label", "Item") }}
              </th>
              <th class="text-center">
                {{ $t("wallet:finances.Cashier.NotPaid.label", "Not paid") }}
              </th>
              <th class="text-center">
                {{ $t("wallet:finances.Cashier.Paid.label", "Paid") }}
              </th>
              <th class="text-center">
                {{ $t("wallet:finances.Cashier.Returned.label", "Returned") }}
              </th>
              <th class="text-center">
                {{ $t("wallet:finances.Cashier.Total.label", "Total") }}
              </th>
              <template v-if="event.useOnlinePaymentsForPrizeMoney">
                <th class="text-center">
                  {{ $t("wallet:finances.Cashier.NotPaid.label", "Not paid") }}
                </th>
                <th class="text-center">
                  {{ $t("wallet:finances.Cashier.Paid.label", "Paid") }}
                </th>
                <th class="text-center">
                  {{ $t("wallet:finances.Cashier.Total.label", "Total") }}
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <template v-for="item in balances">
              <template v-if="item.registration">
                <tr :key="item.id">
                  <td class="border-right: 1px solid transparent;">
                    <div>
                      <b>
                        {{
                          $t(
                            "wallet:finances.Cashier.Registration.label",
                            "Registration"
                          )
                        }}
                      </b>
                    </div>
                  </td>
                  <td colspan="10" class="no-border-x text-right">
                    <div class="d-flex justify-end" style="align-items: center">
                      <b class="mr-2">
                        {{
                          $t(
                            "wallet:finances.Cashier.Athletes.label",
                            "Athlete(s)"
                          )
                        }}
                      </b>
                      <AppShowPeopleList
                        :people="item.registration.athletes"
                        show-name-only
                      />

                      <span class="mx-2">-</span>
                      <b class="mr-2">
                        {{
                          $t("wallet:finances.Cashier.Horses.label", "Horse(s)")
                        }}
                      </b>
                      <template
                        v-if="item.registration.horses.some(h => h.isHorseSlot)"
                      >
                        <AppHorseSlot />
                      </template>
                      <AppShowHorsesList
                        v-else
                        :horses="item.registration.horses"
                        show-name-only
                      />
                    </div>
                  </td>
                </tr>
              </template>
              <tr
                v-for="balance in item.balances"
                :key="balance.id + 'balance' + item.id"
                class="cell-texts-center"
              >
                <template v-if="balance.itemType === 'Entry'">
                  <td>
                    <div class="muted--text">
                      {{
                        $t(
                          "wallet:finances.Cashier.Competition.label",
                          "Competition:"
                        )
                      }}
                      {{ balance.code }}
                      <span v-if="balance.name"
                        >&nbsp;| {{ balance.name }}</span
                      >
                      <div>
                        <AppShowEntryResolution
                          v-if="balance.entry"
                          :entry="balance.entry"
                        />
                      </div>

                      <div>
                        <small
                          >{{
                            $t(
                              "wallet:finances.Cashier.registeredAtTimeStamp.label",
                              "Registered:"
                            )
                          }}<br />
                          {{
                            $dayjs.date(balance.timestamp, "fullTime")
                          }}</small
                        >
                      </div>
                    </div>
                  </td>
                  <td>
                    {{ showAmount(balance.incomeNotPaidCents) }}
                  </td>
                  <td>{{ showAmount(balance.incomePaidCents) }}</td>
                  <td>{{ showAmount(0 - balance.incomeReturnedCents) }}</td>
                  <td>
                    {{
                      showAmount(
                        balance.incomeNotPaidCents +
                          balance.incomePaidCents -
                          balance.incomeReturnedCents
                      )
                    }}
                  </td>
                  <template v-if="event.useOnlinePaymentsForPrizeMoney">
                    <td>
                      {{ showAmount(balance.expenseNotPaidCents) }}
                    </td>
                    <td>
                      {{ showAmount(balance.expensePaidCents) }}
                    </td>
                    <td>
                      {{
                        showAmount(
                          balance.expenseNotPaidCents + balance.expensePaidCents
                        )
                      }}
                    </td>
                  </template>
                </template>
                <template
                  v-else-if="balance.itemType === 'RegistrationEventProduct'"
                >
                  <td>
                    <div class="muted--text">
                      <div>
                        {{ balance.name }}
                        <template v-if="balance.competition"
                          >(<AppShowCompetitionCodeAndName
                            :competition="balance.competition"
                          />)</template
                        >
                      </div>

                      <div>
                        <small
                          >{{
                            $t(
                              "wallet:finances.Cashier.registeredAtTimeStamp.label",
                              "Registered:"
                            )
                          }}<br />
                          {{
                            $dayjs.date(balance.timestamp, "fullTime")
                          }}</small
                        >
                      </div>
                    </div>
                  </td>
                  <td>
                    {{ showAmount(balance.incomeNotPaidCents) }}
                  </td>
                  <td>{{ showAmount(balance.incomePaidCents) }}</td>
                  <td>{{ showAmount(0 - balance.incomeReturnedCents) }}</td>
                  <td>
                    {{
                      showAmount(
                        balance.incomeNotPaidCents +
                          balance.incomePaidCents -
                          balance.incomeReturnedCents
                      )
                    }}
                  </td>
                  <template v-if="event.useOnlinePaymentsForPrizeMoney">
                    <td>
                      {{ showAmount(balance.expenseNotPaidCents) }}
                    </td>
                    <td>
                      {{ showAmount(balance.expensePaidCents) }}
                    </td>
                    <td>
                      {{
                        showAmount(
                          balance.expenseNotPaidCents + balance.expensePaidCents
                        )
                      }}
                    </td>
                  </template>
                </template>
              </tr>
            </template>
          </tbody>
        </table>

        <br />
        <br />
        <v-sheet color="alternate" tile>
          <table>
            <colgroup>
              <col style="width: 165px" />
            </colgroup>
            <colgroup>
              <col style="width: 150px" />
              <col style="width: 150px" />
              <col style="width: 150px" />
              <col style="width: 88px" />
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th colspan="4" class="text-center">
                  {{
                    $t(
                      "wallet:finances.Cashier.RegistrationsAndProducts.label",
                      "Registrations and products"
                    )
                  }}
                </th>
                <template v-if="event.useOnlinePaymentsForPrizeMoney">
                  <th colspan="3" class="text-center">
                    {{
                      $t(
                        "wallet:finances.Cashier.PrizeMoney.label",
                        "Prize money"
                      )
                    }}
                  </th>
                </template>
              </tr>
              <tr>
                <th></th>
                <th class="text-center">
                  {{ $t("wallet:finances.Cashier.NotPaid.label", "Not paid") }}
                </th>
                <th class="text-center">
                  {{ $t("wallet:finances.Cashier.Paid.label", "Paid") }}
                </th>
                <th class="text-center">
                  {{ $t("wallet:finances.Cashier.Returned.label", "Returned") }}
                </th>
                <th class="text-center">
                  {{ $t("wallet:finances.Cashier.Total.label", "Total") }}
                </th>
                <template v-if="event.useOnlinePaymentsForPrizeMoney">
                  <th class="text-center">
                    {{
                      $t("wallet:finances.Cashier.NotPaid.label", "Not paid")
                    }}
                  </th>
                  <th class="text-center">
                    {{ $t("wallet:finances.Cashier.Paid.label", "Paid") }}
                  </th>
                  <th class="text-center">
                    {{ $t("wallet:finances.Cashier.Total.label", "Total") }}
                  </th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr class="cell-texts-center">
                <td>
                  <b>
                    {{
                      $t(
                        "wallet:finances.Cashier.AllPayables.label",
                        "All payables"
                      )
                    }}
                  </b>
                </td>
                <td>{{ allPayables("incomeNotPaid") }}</td>
                <td>{{ allPayables("incomePaid") }}</td>
                <td>{{ allPayables("incomeReturned") }}</td>
                <td>{{ allPayables("incomeTotal") }}</td>
                <template v-if="event.useOnlinePaymentsForPrizeMoney">
                  <td></td>
                  <td></td>
                  <td></td>
                </template>
              </tr>
              <template v-if="returns">
                <tr class="no-hover">
                  <th></th>
                  <th class="text-center" style="font-size: 14px !important">
                    {{
                      $t("wallet:finances.Cashier.Available.label", "Available")
                    }}
                  </th>
                  <th class="text-center" style="font-size: 14px !important">
                    {{
                      $t(
                        "wallet:finances.Cashier.UsedorPaidOut.label",
                        "Used or paid out"
                      )
                    }}
                  </th>
                  <th></th>
                  <th class="text-center" style="font-size: 14px !important">
                    {{ $t("wallet:finances.Cashier.Total.label", "Total") }}
                  </th>
                  <template v-if="event.useOnlinePaymentsForPrizeMoney">
                    <th></th>
                    <th></th>
                    <th></th>
                  </template>
                </tr>
                <tr class="cell-texts-center">
                  <td>
                    <b>
                      {{
                        $t(
                          "wallet:finances.Cashier.ReturnsUsage.label",
                          "Usage of returns"
                        )
                      }}
                    </b>
                  </td>
                  <td>{{ showAmount(0 - returns.incomeNotPaidCents) }}</td>
                  <td>{{ showAmount(0 - returns.incomePaidCents) }}</td>
                  <td></td>
                  <td></td>
                  <template v-if="event.useOnlinePaymentsForPrizeMoney">
                    <td></td>
                    <td></td>
                    <td></td>
                  </template>
                </tr>
              </template>
              <tr class="cell-texts-center">
                <td>
                  <b>
                    {{
                      $t(
                        "wallet:finances.Cashier.TotalVAT.label",
                        "Total, incl VAT"
                      )
                    }}
                  </b>
                </td>
                <td>{{ totals("incomeNotPaid") }}</td>
                <td>{{ totals("incomePaid") }}</td>
                <td></td>
                <td>
                  <b>{{ totals("incomeTotal") }}</b>
                </td>
                <template v-if="event.useOnlinePaymentsForPrizeMoney">
                  <td>{{ totals("expenseNotPaid") }}</td>
                  <td>{{ totals("expensePaid") }}</td>
                  <td>
                    <b>{{ totals("expenseTotal") }}</b>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </v-sheet>

        <br />
        <br />
      </div>
      <div v-else class="cashier-mobile-details">
        <h3 class="pa-3">{{ person.name }}</h3>

        <div class="mx-n3">
          <template v-for="item in balances">
            <AppTableHeader :key="item.id + 'registration-info'" single-col>
              <template v-if="item.registration" #left>
                <div class="d-flex flex-column">
                  <b>
                    {{
                      $t(
                        "wallet:finances.Cashier.Registration.label",
                        "Registration"
                      )
                    }}
                  </b>
                  <div class="mt-1" style="font-weight: normal">
                    <AppShowPeopleList
                      :people="item.registration.athletes"
                      show-name-only
                    />
                  </div>
                  <div class="mt-1" style="font-weight: normal">
                    <template
                      v-if="item.registration.horses.some(h => h.isHorseSlot)"
                    >
                      <AppHorseSlot />
                    </template>
                    <AppShowHorsesList
                      v-else
                      :horses="item.registration.horses"
                      show-name-only
                    />
                  </div>
                </div>
              </template>
            </AppTableHeader>
            <AppTable :key="item.id" :items="balances">
              <template #colgroup>
                <colgroup>
                  <col style="width: 48%" />
                  <col style="width: 4%" />
                  <col style="width: 48%" />
                </colgroup>
              </template>
              <template #items="{ toggleItemExpand, itemIsExpanded }">
                <template v-for="balance in item.balances">
                  <tr :key="balance.id + 'balance'">
                    <td>
                      <template v-if="balance.itemType === 'Entry'">
                        <div>
                          <div>
                            {{
                              $t(
                                "wallet:finances.Cashier.Competition.label",
                                "Competition:"
                              )
                            }}
                            {{ balance.code }}
                            <span v-if="balance.name">
                              | {{ balance.name }}</span
                            >
                          </div>

                          <div class="muted--text">
                            <AppShowEntryResolution
                              v-if="balance.entry"
                              :entry="balance.entry"
                            />
                          </div>

                          <div class="muted--text">
                            <small>
                              {{
                                $t(
                                  "wallet:finances.Cashier.registeredAtTimeStamp.label",
                                  "Registered:"
                                )
                              }}<br />
                              {{ $dayjs.date(balance.timestamp, "fullTime") }}
                            </small>
                          </div>
                        </div>
                      </template>
                      <template
                        v-else-if="
                          balance.itemType === 'RegistrationEventProduct'
                        "
                      >
                        <div>
                          <div>
                            {{ balance.name }}
                            <template v-if="balance.competition"
                              >(<AppShowCompetitionCodeAndName
                                :competition="balance.competition"
                              />)</template
                            >
                          </div>

                          <div>
                            <small>
                              {{
                                $t(
                                  "wallet:finances.Cashier.registeredAtTimeStamp.label",
                                  "Registered:"
                                )
                              }}<br />
                              {{ $dayjs.date(balance.timestamp, "fullTime") }}
                            </small>
                          </div>
                        </div>
                      </template>
                    </td>

                    <TableVerticalDivider tag="td" class="ma-auto" />

                    <td>
                      <v-row no-gutters>
                        <v-col>
                          <template
                            v-if="
                              balance.incomeNotPaidCents +
                              balance.incomePaidCents -
                              balance.incomeReturnedCents
                            "
                          >
                            <b>{{
                              $t("wallet:finances.Cashier.Total.label", "Total")
                            }}</b>
                            <br />
                            {{
                              showAmount(
                                balance.incomeNotPaidCents +
                                  balance.incomePaidCents -
                                  balance.incomeReturnedCents
                              )
                            }}
                          </template>
                        </v-col>
                        <v-col class="text-end align-self-center">
                          <v-btn
                            color="primary"
                            icon
                            outlined
                            small
                            @click="toggleItemExpand(balance.id)"
                            ><v-icon size="small">{{
                              itemIsExpanded(balance.id)
                                ? "$iconCaretDown"
                                : "$iconCaretRight"
                            }}</v-icon></v-btn
                          >
                        </v-col>
                      </v-row>
                    </td>
                  </tr>

                  <tr
                    v-if="itemIsExpanded(balance.id)"
                    :key="balance.id + 'expanded'"
                    class="bg-primary text-white"
                    style="
                      box-shadow: inset var(--v-primary-darken2) 0px -8px 25px;
                    "
                  >
                    <td
                      style="vertical-align: top"
                      class="mobile-padding-right"
                    >
                      <div class="mb-4">
                        <b>{{
                          $t("wallet:finances.Cashier.Fees.label", "Fees")
                        }}</b>
                      </div>

                      <AppChipFull
                        v-if="balance.incomeNotPaidCents"
                        color="secondary"
                        class="mb-2"
                        :class="{
                          'mb-2': balance.incomePaidCents >= 0
                        }"
                      >
                        <div>
                          <b>{{
                            $t(
                              "wallet:finances.Cashier.NotPaid.label",
                              "Not paid"
                            )
                          }}</b>
                        </div>
                        <div>
                          {{ showAmount(balance.incomeNotPaidCents) }}
                        </div>
                      </AppChipFull>
                      <AppChipFull
                        v-if="balance.incomePaidCents"
                        color="accent"
                        :class="{
                          'mb-2': balance.incomeReturnedCents >= 0
                        }"
                      >
                        <div>
                          <b>{{
                            $t("wallet:finances.Cashier.Paid.label", "Paid")
                          }}</b>
                        </div>
                        <div>{{ showAmount(balance.incomePaidCents) }}</div>
                      </AppChipFull>
                      <AppInfoChipOutlined
                        v-if="balance.incomeReturnedCents"
                        color="white"
                      >
                        <div>
                          <b>{{
                            $t(
                              "wallet:finances.Cashier.Returned.label",
                              "Returned"
                            )
                          }}</b>
                        </div>
                        <div>
                          {{ showAmount(0 - balance.incomeReturnedCents) }}
                        </div>
                      </AppInfoChipOutlined>
                    </td>

                    <TableVerticalDivider tag="td" color="white" />
                    <td style="vertical-align: top" class="mobile-padding-left">
                      <template v-if="event.useOnlinePaymentsForPrizeMoney">
                        <div class="mb-4">
                          <b>{{
                            $t(
                              "wallet:finances.Cashier.PrizeMoney.label",
                              "Prize money"
                            )
                          }}</b>
                        </div>
                        <AppInfoChipOutlined
                          v-if="
                            balance.expenseNotPaidCents +
                            balance.expensePaidCents
                          "
                          color="white"
                          class="mb-2"
                        >
                          <div>
                            <b>{{
                              $t("wallet:finances.Cashier.Total.label", "Total")
                            }}</b>
                          </div>
                          <div>
                            {{
                              showAmount(
                                balance.expenseNotPaidCents +
                                  balance.expensePaidCents
                              )
                            }}
                          </div>
                        </AppInfoChipOutlined>
                      </template>
                    </td>
                  </tr>
                </template>
              </template>
            </AppTable>
          </template>
          <br />
          <AppTable hide-no-data>
            <template #colgroup>
              <colgroup>
                <col style="width: 48%" />
                <col style="width: 4%" />
                <col style="width: 48%" />
              </colgroup>
            </template>
            <template #items="{ toggleItemExpand, itemIsExpanded }">
              <tr class="primary-light">
                <td>
                  <b>
                    {{
                      $t(
                        "wallet:finances.Cashier.AllPayables.label",
                        "All payables"
                      )
                    }}
                  </b>
                </td>
                <TableVerticalDivider tag="td" />
                <td>
                  <v-row no-gutters>
                    <v-col>
                      <template v-if="allPayables('incomeTotal')">
                        <div>
                          <b>{{
                            $t("wallet:finances.Cashier.Total.label", "Total")
                          }}</b>
                          <br />
                        </div>
                        {{ allPayables("incomeTotal") }}</template
                      >
                    </v-col>
                    <v-col class="text-end align-self-center">
                      <v-btn
                        color="primary"
                        icon
                        outlined
                        small
                        @click="toggleItemExpand(totalsExpands.allPayables)"
                        ><v-icon size="small">{{
                          itemIsExpanded(totalsExpands.allPayables)
                            ? "$iconCaretDown"
                            : "$iconCaretRight"
                        }}</v-icon></v-btn
                      >
                    </v-col>
                  </v-row>
                </td>
              </tr>
              <tr
                v-if="itemIsExpanded(totalsExpands.allPayables)"
                class="primary"
              >
                <td class="white--text" colspan="99">
                  <div class="mb-4">
                    <b>{{
                      $t(
                        "wallet:finances.Cashier.RegistrationsAndProducts.label",
                        "Registrations and products"
                      )
                    }}</b>
                  </div>

                  <AppChipFull
                    v-if="allPayables('incomeNotPaid')"
                    color="secondary"
                    :class="{ 'mb-2': !!allPayables('incomePaid') }"
                  >
                    <div>
                      <b>{{
                        $t("wallet:finances.Cashier.NotPaid.label", "Not paid")
                      }}</b>
                    </div>
                    <div>{{ allPayables("incomeNotPaid") }}</div>
                  </AppChipFull>
                  <AppChipFull
                    v-if="allPayables('incomePaid')"
                    color="accent"
                    :class="{ 'mb-2': !!allPayables('incomeReturned') }"
                  >
                    <div>
                      <b>{{
                        $t("wallet:finances.Cashier.Paid.label", "Paid")
                      }}</b>
                    </div>
                    <div>{{ allPayables("incomePaid") }}</div>
                  </AppChipFull>
                  <AppInfoChipOutlined
                    v-if="allPayables('incomeReturned')"
                    color="white"
                  >
                    <div>
                      <b>{{
                        $t("wallet:finances.Cashier.Returned.label", "Returned")
                      }}</b>
                    </div>
                    <div>{{ allPayables("incomeReturned") }}</div>
                  </AppInfoChipOutlined>
                </td>
              </tr>

              <tr v-if="returns" class="primary-light">
                <td>
                  <b>
                    {{
                      $t(
                        "wallet:finances.Cashier.ReturnsUsage.label",
                        "Usage of returns"
                      )
                    }}
                  </b>
                </td>
                <td></td>
                <td class="text-end">
                  <v-btn
                    v-if="
                      returns.incomeNotPaidCents ||
                      returns.incomePaidCents ||
                      returns.incomePaidCents
                    "
                    color="primary"
                    icon
                    outlined
                    small
                    @click="toggleItemExpand(totalsExpands.usageReturns)"
                    ><v-icon size="small">{{
                      itemIsExpanded(totalsExpands.usageReturns)
                        ? "$iconCaretDown"
                        : "$iconCaretRight"
                    }}</v-icon></v-btn
                  >
                </td>
              </tr>
              <tr
                v-if="itemIsExpanded(totalsExpands.usageReturns)"
                class="primary white--text"
              >
                <td colspan="99" style="padding: 20px">
                  <AppChipFull
                    v-if="returns.incomeNotPaidCents"
                    color="secondary"
                    :class="{ 'mb-2': returns.incomePaidCents >= 0 }"
                  >
                    <div>
                      <b>{{
                        $t(
                          "wallet:finances.Cashier.Available.label",
                          "Available"
                        )
                      }}</b>
                    </div>
                    <div>{{ showAmount(0 - returns.incomeNotPaidCents) }}</div>
                  </AppChipFull>
                  <AppChipFull v-if="returns.incomePaidCents" color="accent">
                    <div>
                      <b>{{
                        $t(
                          "wallet:finances.Cashier.UsedorPaidOut.label",
                          "Used or paid out"
                        )
                      }}</b>
                    </div>
                    <div>{{ showAmount(0 - returns.incomePaidCents) }}</div>
                  </AppChipFull>
                </td>
              </tr>

              <tr class="primary-light">
                <td>
                  <b>{{
                    $t(
                      "wallet:finances.Cashier.TotalVAT.label",
                      "Total, incl VAT"
                    )
                  }}</b>
                </td>
                <TableVerticalDivider tag="td" />
                <td>
                  <v-row no-gutters>
                    <v-col>
                      <div>
                        <b>{{
                          $t("wallet:finances.Cashier.Total.label", "Total")
                        }}</b>
                      </div>
                      {{ totals("incomeTotal") }}
                    </v-col>
                    <v-col class="text-end align-self-center">
                      <v-btn
                        color="primary"
                        icon
                        outlined
                        small
                        @click="toggleItemExpand(totalsExpands.totals)"
                        ><v-icon size="small">{{
                          itemIsExpanded(totalsExpands.totals)
                            ? "$iconCaretDown"
                            : "$iconCaretRight"
                        }}</v-icon></v-btn
                      >
                    </v-col>
                  </v-row>
                </td>
              </tr>
              <tr
                v-if="itemIsExpanded(totalsExpands.totals)"
                class="primary white--text"
              >
                <td style="vertical-align: top" class="mobile-padding-right">
                  <div class="mb-4">
                    <b>{{
                      $t("wallet:finances.Cashier.Fees.label", "Fees")
                    }}</b>
                  </div>

                  <AppChipFull
                    v-if="totals('incomeNotPaid')"
                    color="secondary"
                    :class="{ 'mb-2': !!totals('incomePaid') }"
                  >
                    <div>
                      <b>{{
                        $t("wallet:finances.Cashier.NotPaid.label", "Not paid")
                      }}</b>
                    </div>
                    <div>
                      {{ totals("incomeNotPaid") }}
                    </div>
                  </AppChipFull>
                  <AppChipFull
                    v-if="totals('incomePaid')"
                    color="accent"
                    class="mb-2"
                  >
                    <div>
                      <b>{{
                        $t("wallet:finances.Cashier.Paid.label", "Paid")
                      }}</b>
                    </div>
                    <div>{{ totals("incomePaid") }}</div>
                  </AppChipFull>
                </td>
                <TableVerticalDivider tag="td" color="white" />
                <td style="vertical-align: top" class="mobile-padding-left">
                  <template v-if="event.useOnlinePaymentsForPrizeMoney">
                    <div class="mb-4">
                      <b>{{
                        $t(
                          "wallet:finances.Cashier.PrizeMoney.label",
                          "Prize money"
                        )
                      }}</b>
                    </div>
                    <AppInfoChipOutlined
                      v-if="totals('expenseTotal')"
                      color="primary-lighten2"
                    >
                      <div>
                        <b>{{
                          $t("wallet:finances.Cashier.Total.label", "Total")
                        }}</b>
                      </div>
                      <div>
                        {{ totals("expenseTotal") }}
                      </div>
                    </AppInfoChipOutlined>
                  </template>
                </td>
              </tr>
            </template>
          </AppTable>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
export default {
  name: "PersonCashierDetails"
};
</script>
<script setup>
import { useCashierHelpers } from "./cashier";
import AppShowCompetitionCodeAndName from "@AppShows/event/AppShowCompetitionCodeAndName.vue";
import AppLoadingPage from "@/components/elements/AppLoadingPage.vue";
import AppShowEntryResolution from "@AppShows/AppShowEntryResolution.vue";
import AppShowPeopleList from "@AppShows/person/AppShowPeopleList.vue";
import AppShowHorsesList from "@AppShows/horse/AppShowHorsesList.vue";
import AppHorseSlot from "@elements/AppHorseSlot.vue";
import "./cashier.scss";

import AppTable from "@elements/table/AppTable.vue";
import AppTableHeader from "@elements/AppTableHeader.vue";
import TableVerticalDivider from "@elements/table/TableVerticalDivider.vue";

import AppInfoChipOutlined from "@elements/chips/AppInfoChipOutlined.vue";
import AppChipFull from "@elements/chips/AppChipFull.vue";

const props = defineProps({
  eventId: {
    type: String,
    required: true
  },
  person: {
    type: Object,
    required: true
  },
  ignoreOrganiser: {
    type: Boolean,
    default: false
  }
});

const {
  masterCurrency,

  balances,
  returns,

  unregister,
  allPayables,
  totals,
  showAmount,

  cashierLoading,
  cashierRefetch,

  event,
  eventLoading,
  eventRefetch
} = useCashierHelpers(props);

const totalsExpands = {
  allPayables: "allPayables",
  usageReturns: "usageReturns",
  totals: "totals"
};
</script>
